/**
 * Shared webhook-config query/mutation hooks — this is the template every
 * other src/queries/*.js file follows. Previously duplicated (separate state +
 * separate fetch/create/toggle/delete logic) between
 * pages/Settings/components/Webhooks/index.jsx and
 * pages/Tasks/components/PromptModal/components/PromptDetailsForm.jsx.
 *
 * Writes patch the list query's cache directly (queryClient.setQueryData)
 * instead of invalidating + refetching, matching the optimistic-local-update
 * behavior both original call sites already had.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const webhookKeys = {
  all: ["webhookConfigs"],
  list: (userId, accountId) => [...webhookKeys.all, "list", userId, accountId],
};

export function useWebhookConfigs(userId, accountId) {
  return useQuery({
    queryKey: webhookKeys.list(userId, accountId),
    queryFn: async () => {
      const query = accountId
        ? `user_id=${userId}&account_id=${accountId}`
        : `user_id=${userId}`;
      const res = await request(GET, "/webhook/configure", { query });
      return res?.data?.webhooks || [];
    },
    enabled: !!userId,
  });
}

export function useCreateWebhookConfig(userId, accountId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (webhook_name) =>
      request(POST, "/webhook/configure", {
        payload: { user_id: userId, account_id: accountId, webhook_name },
      }).then((res) => res?.data),
    onSuccess: (created) => {
      queryClient.setQueryData(webhookKeys.list(userId, accountId), (current = []) => [
        {
          source_id: created.source_id,
          webhook_name: created.webhook_name,
          active: created.active,
          configured_at: created.configured_at,
        },
        ...current,
      ]);
    },
  });
}

export function useToggleWebhookConfig(userId, accountId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ source_id, active }) =>
      request(PUT, `/webhook/configure/${source_id}`, {
        payload: { user_id: userId, active },
      }),
    onSuccess: (_data, { source_id, active }) => {
      queryClient.setQueryData(webhookKeys.list(userId, accountId), (current = []) =>
        current.map((hook) => (hook.source_id === source_id ? { ...hook, active } : hook))
      );
    },
  });
}

export function useDeleteWebhookConfig(userId, accountId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (source_id) =>
      request(DELETE, `/webhook/configure/${source_id}`, { query: `user_id=${userId}` }),
    onSuccess: (_data, source_id) => {
      queryClient.setQueryData(webhookKeys.list(userId, accountId), (current = []) =>
        current.filter((hook) => hook.source_id !== source_id)
      );
    },
  });
}

// Secret is fetched on demand (a "View details" click), not on mount — modeled
// as a mutation rather than a query since there's nothing to cache/re-render on.
export function useWebhookConfigSecret(userId) {
  return useMutation({
    mutationFn: (source_id) =>
      request(GET, `/webhook/configure/${source_id}/secret`, {
        query: `user_id=${userId}`,
      }).then((res) => res?.data),
  });
}
